import messages from "messages";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  MouseEventHandler,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button } from "react-bootstrap";
// components
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import AvailableTimePeriodEditor from "../../../available-time-period-editor/AvailableTimePeriodEditor";
import AvailableTimePeriodsViewer from "../../../available-time-periods-viewer/AvailableTimePeriodsViewer";
// utils
import getClasses from "utils/getClasses";
// styles
import styles from "./ScheduleEditor.module.scss";
import { AmplitudePageName, track } from "utils/amplitude";

const ANALYTICS_COMPONENT_NAME = "availability-editor";

type Props = {
  canApply: boolean;
  canCancel: boolean;
  activeIndex: number | null;
  onApply: MouseEventHandler;
  onChange: (arg: ParsedAvailableTimePeriod) => void;
  onCopy: (arg: number) => void;
  onCreate: () => void;
  onDelete: (arg: number) => void;
  onSelect: (arg: number) => void;
  value: ParsedAvailableTimePeriod[];
  timezone: TimeZone;
  analyticsPageName: AmplitudePageName;
} & ComponentPropsWithoutRef<typeof ModalLayout>;

/**
 * renders UI to view / edit schedule / available time period
 * includes date range, weekly hours and booking restrictions
 */
const ForwardedScheduleEditor = forwardRef<
  Pick<HTMLFormElement, "checkValidity" | "reset">,
  Props
>(function ScheduleEditor(
  {
    canApply,
    canCancel,
    activeIndex,
    onApply,
    onChange,
    onCopy,
    onCreate,
    onDelete,
    onHide,
    onSelect,
    value,
    show,
    timezone,
    analyticsPageName,
  },
  ref,
) {
  const [validities, setValidities] = useState<(boolean | undefined)[]>([]);
  const inputRefs = useRef<
    (Pick<HTMLFormElement, "checkValidity" | "reset"> | null)[]
  >([]);

  useImperativeHandle(ref, () => ({
    checkValidity: () => {
      const nextValidities = inputRefs.current.map((el) => {
        if (!el) {
          return true;
        }

        return el.checkValidity();
      });

      setValidities(nextValidities);
      return nextValidities.every((el) => el);
    },
    reset: () => {
      setValidities([]);
      return inputRefs.current.forEach((el) => {
        if (!el) {
          return true;
        }
        return el.reset();
      });
    },
  }));

  return (
    <ModalLayout
      show={show}
      size={"xl"}
      backdrop={"static"}
      keyboard={false}
      Header={messages.myAvailability}
      showHeaderDivider
      dialogClassName={styles["schedule-editor__dialog"]}
      headerClass={styles["schedule-editor__header"]}
      bodyClass={styles["schedule-editor__body"]}
      footerClass={styles["schedule-editor__footer"]}
      Footer={
        <>
          <Button
            variant={"outline-primary"}
            className={styles["schedule-editor__action-btn"]}
            onClick={() => {
              track(
                analyticsPageName,
                [ANALYTICS_COMPONENT_NAME, "cancel-button"],
                "click",
              );
              onHide();
            }}
          >
            {messages.cancel}
          </Button>
          <Button
            className={styles["schedule-editor__action-btn"]}
            onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
              track(
                analyticsPageName,
                [ANALYTICS_COMPONENT_NAME, "save-and-close-button"],
                "click",
              );
              onApply(event);
            }}
            disabled={!canApply}
          >
            {messages.saveAndClose}
          </Button>
        </>
      }
      disabled={!canCancel}
    >
      {/* schedules / available type periods list */}
      <div className={styles["schedule-editor__sidebar"]}>
        <AvailableTimePeriodsViewer
          activeIndex={activeIndex}
          timezone={timezone}
          validities={validities}
          value={value}
          onCopy={onCopy}
          onCreate={onCreate}
          onDelete={onDelete}
          onSelect={onSelect}
          analyticsPageName={analyticsPageName}
          analyticsComponentName={ANALYTICS_COMPONENT_NAME}
        />
      </div>

      {/* editor for each schedule / available type period */}
      <div className={styles["schedule-editor__editor"]}>
        {activeIndex === null ? (
          <p
            className={getClasses(
              styles["schedule-editor__empty"],
              styles[
                `schedule-editor__empty--${
                  !value.length ? "no-schedule" : "no-edit"
                }`
              ],
            )}
          >
            {!value.length
              ? messages.clickCreateNew
              : messages.selectTheSchedule}
          </p>
        ) : (
          <>
            {value.map((el, index) => (
              <AvailableTimePeriodEditor
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                value={el}
                onApply={onChange}
                hidden={activeIndex !== index}
                onValidityChange={(el) =>
                  setValidities((s) => {
                    s[index] = el;
                    return [...s];
                  })
                }
                analyticsPageName={analyticsPageName}
              />
            ))}
          </>
        )}
      </div>
    </ModalLayout>
  );
});

export default ForwardedScheduleEditor;
